import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, lastValueFrom, Observable, tap } from 'rxjs';
import { JwtHelperService } from '@auth0/angular-jwt';

import { base } from '../api-config';

import { User, UserLogin } from '../models/user';
import { LoginResponse } from '../models/token';

import { MessageService } from './message.service';
import {
  ConfirmationDialogComponent
} from "../../components/general-purpose/dialogs/confirmation-dialog/confirmation-dialog.component";
import {Dialog} from "@angular/cdk/dialog";

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private promptingLogout = false;
  private refreshScheduled = false;
  private loggedInUserSource = new BehaviorSubject<User | null>(null);

  loggedInUser$ = this.loggedInUserSource.asObservable();

  constructor(private router: Router,
              private http: HttpClient,
              private cdkdialog: Dialog,
              private jwt: JwtHelperService,
              private msgService: MessageService) {

    const user = this.getUser();
    this.loggedInUserSource.next(user);
  }

  shibboleth_login(): Observable<LoginResponse> {
    return this.http.get<LoginResponse>(`${base}/token`,
      {
        withCredentials: true,
      }).pipe(
      tap(res => {
        const accessToken = res['access_token'];
        const refreshToken = res['refresh_token'];

        if (!this.isValidToken(accessToken) || !this.isValidToken(refreshToken))
          throw new Error('The server returned an expired access token!');

        this.initSession(accessToken, refreshToken);
      })
    );
  }

  guest_login(credentials: UserLogin): Observable<LoginResponse> {
    const params = new URLSearchParams();
    params.set('username', credentials.email);
    params.set('password', credentials.password);

    const contentType = 'application/x-www-form-urlencoded';
    const route = `${base}/login`;
    const options = {
      headers: new HttpHeaders().set('Content-Type', contentType)
    };

    return this.http.post<LoginResponse>(route, params.toString(), options).pipe(
      tap(res => {
        const accessToken = res['access_token'];
        const refreshToken = res['refresh_token'];

        if (!this.isValidToken(accessToken) || !this.isValidToken(refreshToken))
          throw new Error('The server returned an expired access token!');

        this.initSession(accessToken, refreshToken);
      })
    );
  }


  async refresh(promptLogout: boolean = true): Promise<string | null> {
      const refreshToken = this.getRefreshToken();
      let newAccessToken = null;

      if (!refreshToken || this.jwt.isTokenExpired(refreshToken)) {
        if (promptLogout) this.promptLogout();
        return null;
      }

      try {

        newAccessToken = await this.getRefreshedAccessToken(refreshToken);
        const decoded = this.jwt.decodeToken(newAccessToken);
        const user = this.getUser();

        if (!decoded || this.jwt.isTokenExpired(newAccessToken)) {
          const msg = 'Server returned an invalid token during refresh.';
          this.msgService.log(msg);
          this.msgService.showSimple(msg);
          return null;
        }

        if (user && decoded.id !== user.id) {
          const msg = 'Server returned token for different user.';
          this.msgService.log(msg);
          this.msgService.showSimple(msg);
          return null;
        }

        this.storeAccessToken(newAccessToken);

      } catch (error: any) {
        this.msgService.log(error);
      }

      return newAccessToken;
  }

  promptLogout(): void {
    if (this.promptingLogout)
      return;

    this.promptingLogout = true;
    const ref = this.cdkdialog.open(ConfirmationDialogComponent, {
      data: {
        title: 'Session expired',
        message: 'You have been logged out automatically.',
        cancel_button: false
      }
    });

    ref.closed.subscribe(async () => {
      await this.logout(false);
      this.promptingLogout = false;
    });
  }

  async getAccessToken(refresh: boolean = true): Promise<string | null> {
    const token = localStorage.getItem('access_token');
    let newAccessToken = null;

    if (!token || !this.jwt.decodeToken(token))
      return null;

    if (!this.jwt.isTokenExpired(token))
      return token;

    try {

      if (refresh)
        newAccessToken = await this.refresh();

    } catch (error: any) {
      this.msgService.log(error);
    }

    return newAccessToken;
  }

  getUser(): User | null {
    const userString = localStorage.getItem('user');

    if (!userString)
    return null;

    return JSON.parse(userString);
  }

  async logout(invalidateRefreshToken: boolean = true): Promise<void> {
    const refreshToken = this.getRefreshToken();
    const accessToken = await this.getAccessToken(false);

    if (invalidateRefreshToken && refreshToken && accessToken)
      await this.invalidate(refreshToken);

    this.clear();
  }

  private invalidate(refreshToken: string): Promise<string> {
    const route = `${base}/logout`;
    const params = new HttpParams()
      .set('refresh_token', refreshToken)
    return lastValueFrom(this.http.post<string>(route, {}, { params }));
  }

  async isLoggedIn(): Promise<boolean> {
    const token = await this.getAccessToken();
    return token !== null;
  }

  private getRefreshToken(): string | null {
    return localStorage.getItem('refresh_token');
  }

  private isValidToken(token: any): boolean {
    return typeof token === 'string' &&
            !!this.jwt.decodeToken(token) &&
            !this.jwt.isTokenExpired(token);
  }

  private initSession(accessToken: string, refreshToken: string): void {
    const decoded = this.jwt.decodeToken(accessToken);
    const roles = decoded.roles;
    const user: User = {
      id: decoded.id,
      email: decoded.email,
      first_name: decoded.first_name,
      last_name: decoded.last_name,
      roles: roles,
      isUser: roles.indexOf('user') !== -1,
      isAdmin: roles.indexOf('admin') !== -1
    };

    this.storeRefreshToken(refreshToken);
    this.storeAccessToken(accessToken);
    this.storeUser(user);
  }

  private getRefreshedAccessToken(refreshToken: string): Promise<string> {
    const route = `${base}/refresh`;
    const params = new HttpParams()
        .set('refresh_token', refreshToken);
    const obs = this.http.post<string>(route, {}, { params });
    return lastValueFrom(obs);
  }

  private storeAccessToken(token: string): void {
    const expiresAt = this.jwt.getTokenExpirationDate(token);

    if (!expiresAt)
      return;

    const expirationDate = new Date(expiresAt);
    const now = Date.now();
    const timeout = expirationDate.getTime() - now - 30 * 1000;
    localStorage.setItem('access_token', token);

    if (!this.getRefreshToken() || timeout <= 1000)
      return;

    if (this.refreshScheduled)
      return;

    this.refreshScheduled = true;

    setTimeout(async () => {
      if (this.getUser())
        await this.refresh();
      this.refreshScheduled = false;
    }, timeout);
  }

  private storeRefreshToken(token: string): void {
    localStorage.setItem('refresh_token', token);
  }

  private storeUser(user: User): void {
    this.loggedInUserSource.next(user);
    localStorage.setItem('user', JSON.stringify(user));
  }

  private clearAccessToken(): void {
    localStorage.removeItem('access_token');
  }

  private clearRefreshToken(): void {
    localStorage.removeItem('refresh_token');
  }

  private clearUser(): void {
    this.loggedInUserSource.next(null);
    localStorage.removeItem('user');
  }

  private clear(): void {
    this.clearAccessToken();
    this.clearRefreshToken();
    this.clearUser();
    this.router.navigateByUrl('login');
  }
}
